if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event.detail?.currentElement || !this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`li[data-media-id="${mediaId}"]:not([hidden])`) ||
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not([hidden])`);

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        if (!options.preventScroll) {
          this.preventStickyHeader();
          window.setTimeout(() => {
            if (this.elements.thumbnails) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
              activeMedia.scrollIntoView({ behavior: 'smooth' });
            }
          });
        } else if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      filterMediaByVariantSize(variantSize, preferredMediaId, options = {}) {
        if (this.dataset.goatSizeFilteredGallery !== 'true' || !variantSize) return;

        const normalizedSize = variantSize.toLowerCase().replace(/\s+/g, '');
        const viewerItems = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        const thumbnailItems = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          : [];
        const visibleMediaIds = new Set();

        viewerItems.forEach((item) => {
          const mediaSize = (item.dataset.goatMediaSize || '').toLowerCase().replace(/\s+/g, '');
          const shouldHide = Boolean(mediaSize) && mediaSize !== normalizedSize;

          item.hidden = shouldHide;
          item.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');

          if (!shouldHide && item.dataset.mediaId) {
            visibleMediaIds.add(item.dataset.mediaId);
          }
        });

        thumbnailItems.forEach((item) => {
          const shouldHide = item.dataset.target ? !visibleMediaIds.has(item.dataset.target) : false;

          item.hidden = shouldHide;
          item.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
        });

        this.updateVisibleMediaCount();

        const activeMedia = viewerItems.find((item) => item.classList.contains('is-active') && !item.hidden);

        if (!activeMedia) {
          const preferredMedia = preferredMediaId
            ? viewerItems.find((item) => item.dataset.mediaId === preferredMediaId && !item.hidden)
            : null;
          const fallbackMedia = preferredMedia || viewerItems.find((item) => !item.hidden);

          if (fallbackMedia) {
            this.setActiveMedia(fallbackMedia.dataset.mediaId, true, { preventScroll: options.preventScroll === true });
          }
        }

        if (this.elements.viewer.slider && typeof this.elements.viewer.resetPages === 'function') {
          this.elements.viewer.resetPages();
        }

        if (this.elements.thumbnails && this.elements.thumbnails.slider && typeof this.elements.thumbnails.resetPages === 'function') {
          this.elements.thumbnails.resetPages();
        }
      }

      updateVisibleMediaCount() {
        const visibleThumbnails = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]:not([hidden])'))
          : [];
        const visibleMedia = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]:not([hidden])'));
        const activeMediaId = this.elements.viewer.querySelector('li[data-media-id].is-active:not([hidden])')?.dataset.mediaId;
        const current = this.querySelector('.slider-counter--current');
        const total = this.querySelector('.slider-counter--total');

        visibleThumbnails.forEach((thumbnail, index) => {
          thumbnail.dataset.mediaPosition = String(index + 1);
        });

        if (total) {
          total.textContent = String(visibleThumbnails.length || visibleMedia.length || 1);
        }

        if (current && activeMediaId) {
          const activeThumbnailIndex = visibleThumbnails.findIndex((thumbnail) => thumbnail.dataset.target === activeMediaId);
          const activeMediaIndex = visibleMedia.findIndex((item) => item.dataset.mediaId === activeMediaId);
          const activeIndex = activeThumbnailIndex >= 0 ? activeThumbnailIndex : activeMediaIndex;

          current.textContent = String(activeIndex >= 0 ? activeIndex + 1 : 1);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
